import instance from '../../axios/utils.ts/instance';
import { ApiResponse } from '../../../constants/types';
import {
  SITE_CONFIG_KEY,
  SiteConfigItem,
  SiteConfigMap,
  SiteConfigResponse,
} from '../../../constants/types/site-config-type';

// 메모리 캐시라 새로고침 전까지 갱신되지 않는다.
// 즉시 반영이 필요해지면 useBanners 처럼 ETag 조건부 요청으로 교체.
const cache = new Map<string, string>();
const inflight = new Map<string, Promise<void>>();

/**
 * 설정 값(JSON 문자열) 파싱
 * 운영에서 입력한 값에 끝 쉼표/공백이 섞여 있어도 살려서 파싱한다.
 */
const parseJson = (value?: string): unknown => {
  if (!value) return undefined;

  try {
    return JSON.parse(value.trim().replace(/,+$/, ''));
  } catch (err) {
    console.error('사이트 설정 값 파싱 실패', err);
    return undefined;
  }
};

/**
 * 목록형 설정 값 파싱
 * 파싱 실패 시 빈 배열을 반환해 화면이 깨지지 않도록 한다.
 */
export const parseSiteConfigValue = <T = SiteConfigItem>(
  value?: string,
): T[] => {
  const parsed = parseJson(value);
  return Array.isArray(parsed) ? (parsed as T[]) : [];
};

/**
 * 객체형 설정 값 파싱 (예: jebs_story_timeline_stats 의 { asOf, items })
 * 파싱 실패 시 undefined 를 반환해 사용처에서 기본값으로 대체한다.
 */
export const parseSiteConfigObject = <T>(value?: string): T | undefined => {
  const parsed = parseJson(value);
  return parsed && !Array.isArray(parsed) ? (parsed as T) : undefined;
};

const fetchKeys = async (keys: SITE_CONFIG_KEY[]) => {
  const response = await instance.get<ApiResponse<SiteConfigResponse[]>>(
    '/v1/site-config',
    { params: { keys } },
  );

  (response.data.data ?? []).forEach(({ key, value }) => cache.set(key, value));
};

// 같은 틱에 여러 컴포넌트가 요청한 키를 모아 한 번에 호출한다.
// 키 30개 제한은 나누지 않는다 (현재 enum 12개). 넘어가면 청크 분할 추가.
let queued = new Set<SITE_CONFIG_KEY>();
let flush: Promise<void> | null = null;

const enqueue = (keys: SITE_CONFIG_KEY[]) => {
  keys.forEach((key) => queued.add(key));

  if (!flush) {
    flush = Promise.resolve().then(() => {
      const batch = [...queued];
      queued = new Set();
      flush = null;
      return fetchKeys(batch);
    });
  }

  return flush;
};

/**
 * 사이트 설정 조회 (키 1~30개)
 * 이미 받은 키는 재요청하지 않고, 동시에 요청된 키는 모아서 한 번만 호출한다.
 *
 * @param keys 조회할 설정 키
 * @returns 키별 원본 문자열
 */
export const getSiteConfig = async (
  keys: SITE_CONFIG_KEY[],
): Promise<SiteConfigMap> => {
  const missing = keys.filter((key) => !cache.has(key) && !inflight.has(key));

  if (missing.length) {
    const request = enqueue(missing).finally(() =>
      missing.forEach((key) => inflight.delete(key)),
    );
    missing.forEach((key) => inflight.set(key, request));
  }

  await Promise.all(keys.map((key) => inflight.get(key)));

  const configs: SiteConfigMap = {};
  keys.forEach((key) => {
    const value = cache.get(key);
    if (value !== undefined) configs[key] = value;
  });

  return configs;
};
